import React from "react";
import {
    func,
    object,
    string,
} from "prop-types";
import DataQualityFormLayout from "./DataQualityFormLayout";
import BackboneContainer from "data/util/BackboneContainer";
import { asMarionetteView } from "lib/common/hoc/BackboneReact/asMarionetteView";

import Dispatcher from "Dispatcher";
import SearchFormActions from "data/SearchFormActions";
import SearchFormStore from "data/SearchFormStore";

import { appAlert } from "lib/withOver";
import userStore from "data/userStore";

export class DataQualityForm extends React.Component {
    constructor(props) {
        super(props);

        this.loadStore = props.loadSearchFormStore.bind(this);
        this.onExport = props.onXLSExportClick.bind(this, props);
        this.onSubmit = props.onFormSubmit.bind(this, props);
    }

    render() {
        const {
            searchForm
        } = this.props;

        const props = {
            ...this.props,
            loadSearchFormStore: this.loadStore,
            onExport: this.onExport,
            onSubmit: this.onSubmit
        };

        return (
            <div id="form-dqr" className="search-form-layout">
              {searchForm(props)}
            </div>
        );
    }
}

DataQualityForm.propTypes = {
    fluxContext: string.isRequired,
    loadSearchFormStore: func.isRequired,
    model: object,
    onFormSubmit: func.isRequired,
    onXLSExportClick: func.isRequired,
    pageModel: object,
    searchForm: func.isRequired,
};

export const DataQualityFormContainer = asMarionetteView(BackboneContainer.createFunctional(
    props => <DataQualityForm {...props} />,
    props => [
    ],
    (prevState, props) => {
        const fluxContext = "CTX_dqr";
        const pageSizeModel = new Backbone.Model({
                key: "pageSize",
                name: "Page Size",
                type: "integer",
                value: props.model.get("pageSize")
        });

        const loadSearchFormStore = stereotypeModel => {
            // load up the SearchFormStore from a given stereotype.
            const key = stereotypeModel.get("key");
            Dispatcher.contextDispatch(fluxContext, {
                type: SearchFormActions.SELECT_STEREOTYPE,
                key: key
            });
        };

        loadSearchFormStore(props.model.get("stereotype"));

        // yes, this does smell, however it's necessary
        // in order to improve testability. this way
        // ReportOptions is decoupled from DataQualityForm
        // and thus there is no need to pass in a lot of
        // ReportOptions related props in the DataQualityForm tests.
        // although one could argue whether that's worth the smell,
        // since the number of unit tests is low and they are
        // rather primitive...
        const searchForm = props => (
            <ReportOptions {...props}/>
        );

        return {
            ...props,
            fluxContext: fluxContext,
            reportTypeOptionsCollection: props.model.get("reportTypeOptionsCollection"),
            validationTypeOptionCollection: props.model.get("validationTypeOptionCollection"),
            stereotype: props.model.get("stereotype"),
            searchForm: searchForm,
            pageModel: pageSizeModel,
            loadSearchFormStore: loadSearchFormStore,
            onFormSubmit: (passedProps, e) => {
                e.preventDefault();
                e.stopPropagation();

                const {
                    fluxContext,
                    model,
                    pageModel,
                    search,
                    checkForm,
                    setModelAttribute
                } = passedProps;

                if (checkForm(fluxContext)) {
                    const query = SearchFormStore.getQ(fluxContext).toAS();

                    // We only pull page size on submit.
                    const pageSize = pageModel.get("value");
                    if (pageSize) {
                        setModelAttribute("pageSize", pageSize);
                    }

                    model.resetPagination();
                    setModelAttribute("sorts", new Backbone.Model());
                    setModelAttribute("searchQuery", query);
                    search();
                }
            },
            onXLSExportClick: (passedProps, e) => {
                e.preventDefault();
                e.stopPropagation();

                const {
                    exportReport,
                    fluxContext,
                    model,
                    checkForm,
                    setModelAttribute
                } = passedProps;

                if (checkForm(fluxContext)) {
                    const query = SearchFormStore.getQ(fluxContext).toAS();

                    model.resetPagination();
                    setModelAttribute("sorts", new Backbone.Model());
                    setModelAttribute("searchQuery", query);
                    exportReport();
                }
            },
            checkForm: fluxContext => {
                // Check if any parameter has been set.
                const hasChangedValue = SearchFormStore.hasValues(fluxContext);

                if (! hasChangedValue) {
                    appAlert("Search requires at least one criterion.");
                }

                return hasChangedValue;
            },
            search: () => {
                Backbone.dispatcher.trigger("search:dataQualityReport", props.model);
            },
            exportReport: () => {
                Backbone.dispatcher.trigger("export:dataQualityReport", props.model);
            },
            setModelAttribute: (attr, val) => {
                Backbone.dispatcher.trigger(
                    "set:dqr:model:attribute",
                    props.model,
                    attr,
                    val
                );
            }
        };
    }
));

const ReportOptions = BackboneContainer.createFunctional(
    props => (
        <DataQualityFormLayout {...props} />
    ),
    props => [
        [props.model, "change:stereotype", "change:reportType", "change:validationType", "change:owners"],
        [props.pageModel, "change:value"]
    ],
    (prevState, props) => {
        // stereotype options/value
        const stereotype = props.model.get("stereotype");
        const stereotypes = props.model.get("stereotypes");
        const searchByValue = stereotype.get("key");
        const searchByOptions = stereotypes.map((item) => {
            const value = item.get("key");
            return {
                value: value,
                // dynamic label
                label: searchByValue === value ? "Search By " + item.get("name") : item.get("name")
            };
        });
        const searchByOnCommitChange = (value) => {
            stereotypes.forEach((model) => {
                const modelKey = model.get("key");
                if (modelKey === value) {
                    props.model.set("stereotype", model);
                    props.loadSearchFormStore(model);
                }
            });
        };

        const reportTypeValue = props.model.get("reportType").get("key");
        const reportTypes = props.model.get("reportTypeOptionsCollection");
        const reportTypeOptions = reportTypes.map((item) => ({
            value: item.get("key"),
            label: item.get("name"),
        }));
        const reportTypeOnCommitChange = (value) => {
            reportTypes.forEach((model) => {
                const modelKey = model.get("key");
                if (modelKey === value) {
                    props.model.set("reportType", model);
                }
            });
        };

        const validationTypeValue = props.model.get("validationType").get("key");
        const validationTypes = props.model.get("validationTypeOptionCollection");
        const firstEntityKey = stereotype.get("implementations")[0].key;
        const illegalLabel = userStore.getIllegalLabel(firstEntityKey);
        const validationTypeOptions = validationTypes.map((item) => ({
            value: item.get("key"),
            label: item.get("template")(illegalLabel),
        }));
        const validationTypeOnCommitChange = (value) => {
            validationTypes.forEach((model) => {
                const modelKey = model.get("key");
                if (modelKey === value) {
                    props.model.set("validationType", model);
                }
            });
        };

        const ownersValue = props.model.get("owners");
        const ownersOnCommitChange = value =>
            props.model.set("owners", value);

        // page value
        const pageSizeValue = props.pageModel.get("value");
        const pageSizeOnCommitChange = (value) => {
            props.pageModel.set("value", value);
        };

        return {
            searchByOnCommitChange,
            searchByOptions,
            searchByValue,

            reportTypeOnCommitChange,
            reportTypeValue,
            reportTypeOptions,

            validationTypeOnCommitChange,
            validationTypeValue,
            validationTypeOptions,

            ownersOnCommitChange,
            ownersValue,

            pageSizeOnCommitChange,
            pageSizeValue,

            fluxContext: props.fluxContext,
            onSubmit: props.onSubmit,
            onExport: props.onExport,
        };
    }
);



// WEBPACK FOOTER //
// ./app/lib/dqr/views/DataQualityForm.js